import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const TermsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const getText = (en, pt) => language === 'pt' ? pt : en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a5c3a] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            {getText('Back', 'Voltar')}
          </button>
          <h1 className="text-3xl font-bold">
            {getText('Terms & Conditions', 'Termos e Condições')}
          </h1>
          <p className="text-white/70 mt-2">
            {getText('Last updated: January 2025', 'Última atualização: Janeiro 2025')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('1. Acceptance of Terms', '1. Aceitação dos Termos')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'By accessing and using Sancur Property Services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.',
                'Ao aceder e utilizar os serviços da Sancur Property Services, aceita e concorda em ficar vinculado a estes Termos e Condições. Se não concordar com estes termos, por favor não utilize os nossos serviços.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('2. Services', '2. Serviços')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {getText(
                'Sancur Property Services provides professional cleaning services including:',
                'A Sancur Property Services fornece serviços profissionais de limpeza incluindo:'
              )}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('Regular house cleaning', 'Limpeza regular de casa')}</li>
              <li>{getText('Deep cleaning', 'Limpeza profunda')}</li>
              <li>{getText('End of tenancy cleaning', 'Limpeza de fim de arrendamento')}</li>
              <li>{getText('Office cleaning', 'Limpeza de escritório')}</li>
              <li>{getText('Upholstery cleaning', 'Limpeza de estofados')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('3. Booking & Payment', '3. Reservas e Pagamento')}
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('All bookings are subject to availability', 'Todas as reservas estão sujeitas a disponibilidade')}</li>
              <li>{getText('Prices quoted are estimates and may vary based on actual work required', 'Os preços apresentados são estimativas e podem variar com base no trabalho real necessário')}</li>
              <li>{getText('Payment is due upon completion of services unless otherwise agreed', 'O pagamento é devido após a conclusão dos serviços, salvo acordo em contrário')}</li>
              <li>{getText('We accept cash, bank transfer, and major credit cards', 'Aceitamos dinheiro, transferência bancária e cartões de crédito principais')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('4. Cancellation Policy', '4. Política de Cancelamento')}
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('Free cancellation up to 24 hours before the scheduled service', 'Cancelamento gratuito até 24 horas antes do serviço agendado')}</li>
              <li>{getText('Cancellations within 24 hours may incur a fee of 50% of the quoted price', 'Cancelamentos dentro de 24 horas podem incorrer numa taxa de 50% do preço orçamentado')}</li>
              <li>{getText('No-shows will be charged the full quoted amount', 'Não comparências serão cobradas o valor total orçamentado')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('5. Our Guarantee', '5. A Nossa Garantia')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'We guarantee your satisfaction. If you are not happy with our service, please contact us within 24 hours and we will return to address any issues at no additional cost.',
                'Garantimos a sua satisfação. Se não estiver satisfeito com o nosso serviço, por favor contacte-nos dentro de 24 horas e voltaremos para resolver quaisquer problemas sem custo adicional.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('6. Liability', '6. Responsabilidade')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'Sancur Property Services is fully insured. In the unlikely event of damage during our service, we will assess and compensate for any damages caused by our staff. Please report any issues within 24 hours of service completion.',
                'A Sancur Property Services está totalmente segurada. No caso improvável de danos durante o nosso serviço, avaliaremos e compensaremos quaisquer danos causados pela nossa equipa. Por favor, reporte quaisquer problemas dentro de 24 horas após a conclusão do serviço.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('7. Access Requirements', '7. Requisitos de Acesso')}
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('You must provide access to your property at the scheduled time', 'Deve fornecer acesso à sua propriedade no horário agendado')}</li>
              <li>{getText('Ensure water and electricity are available', 'Certifique-se de que água e eletricidade estão disponíveis')}</li>
              <li>{getText('Secure valuables and fragile items', 'Guarde objetos de valor e itens frágeis')}</li>
              <li>{getText('Inform us of any pets on the premises', 'Informe-nos sobre quaisquer animais de estimação no local')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('8. Contact', '8. Contacto')}
            </h2>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-gray-700 font-medium">Sancur Property Services</p>
              <p className="text-gray-600">Email: info@sancurservices.com</p>
              <p className="text-gray-600">Phone: +353 89 950 2499</p>
              <p className="text-gray-600">Dublin, Ireland</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsPage;
